import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { ETYPE, POOLS, enemies } from '../entities/enemies';
import { scene } from '../render/scene';
import { attachEnemy } from '../render/enemy-instances';
import { STATE, removeEnemy, spawnRing } from './fx';
import { enemyWeights, variantWeights } from '../data/enemies';
import { rollVariant } from './enemy-ai';
import { clampArena, groundYAt, safeSpot } from '../world/arena';
import { P } from '../world/player';
import { logSeen } from '../platform/storage';
import { resetBossParts } from './boss-parts';
import { densityMulAt, dmgMulAt, hpMulAt } from '../data/pacing';
import { HORDE } from './horde';
/* ============================================================
   生成器 —— 難度只吃時鐘
   ============================================================

   從 `stage-flow.ts` 抽出來的（M8）。抽的理由是循環 import：
   編隊波次要用 `spawnEnemy`，而 `stage-flow` 又要呼叫編隊的排程 ——
   兩邊互相 import 的話，模組初始化順序會變成一個難查的雷。

   這個檔**不 import stage-flow**，所以誰都能安全地用它。 */

/** 這一場的生成狀態：第幾幕（決定地形與配色）、已經出到第幾隻 Boss。
    放在這裡而不是 stage-flow，是因為 Boss 血量的計算要用到它。 */
/** 同屏雜兵上限。Boss 與精英不受限（它們是設計排進去的，不能被雜訊擠掉）。 */
export var ENEMY_CAP = 420;

export var RS = { act: 0, nextBoss: 0 };

/* ---------- 生成 ----------
   難度**只吃時間**（GDD §12.1）。原本是 `stage × 1.35 + 該階段經過秒數`，
   階段推進沒了之後那個式子的前半也沒了。
   一場 20 分鐘，`diff()` 從 0 長到 6。 */
export function diff() { return STATE.t / 200; }

/** 生成權重的表在 `data/enemies.ts`（跟數值與池子容量放在一起）——
    「這一分鐘會遇到什麼」與「牠有多硬」是同一個設計決定的兩半。 */
export function typeWeights(t) { return enemyWeights(t); }
export function rollType(t) {
  var w = typeWeights(t) as Array<[string, number]>, r = RNG.next(), acc = 0;
  for (var i = 0; i < w.length; i++) { acc += w[i][1]; if (r <= acc) return w[i][0]; }
  return w[0][0];
}
export function spawnEnemy(typeName, x, z) {
  var T = ETYPE[typeName];
  /* 同屏上限。140 是 M8 之前的值，而**編隊會被它靜默吃掉** ——
     場上 130 隻時放一波 30 隻的橫掃牆，`spawnEnemy` 全部回 null，
     沒有任何錯誤，玩家只會覺得「剛才那個警告是假的」。
     300 是照 VS 的雜兵軟上限訂的，模擬層量過：每隻 2.8 µs/步、線性，
     300 隻約 0.84ms/步 = 一幀 16.7ms 的 5%。
     **2026-08-02 提到 420**（使用者要「一大波的震撼」）：線性外推約
     1.18ms/步 = 一幀的 7%，而渲染走實例化、draw call 不隨隻數長。
     池子同步加大 —— 池子比上限小的話上限是假的（M8 記過）。 */
  if (!T.boss && enemies.length > ENEMY_CAP) return null;
  var e = POOLS[typeName].get(); if (!e) return null;
  /* **Boss 的血量不吃任何倍率**（B2）。五隻的強度階梯直接寫在
     `data/boss-stats.ts` 的 `hp` 上，跟每一隻的「預計時長」（`want`）並排 ——
     兩個數字要一起看才調得動（`.claude/notes/boss-fight-design.md`）。
     舊版是 `1 + 第幾隻 × 0.55`，階梯藏在生成器裡，改一隻要心算其他四隻。 */
  /* **縮放改成查表**（P5，`data/pacing.ts`）。舊版是兩條單調上升的公式
     `1 + d*0.5 + d²*0.07` 與 `1 + d*0.2`，而 01 §三要的是分段查表 ——
     理由是「每一段可以獨立調」，那正是做得出爽感期谷底的唯一方法。
     表怎麼算出來的、為什麼中段會往下，全寫在 `data/pacing.ts` 的檔頭。 */
  var hpMul = T.boss ? 1 : hpMulAt(STATE.t);
  e.hpMax = T.hp * hpMul; e.hp = e.hpMax;
  /* 經驗跟著實際血量走（理由寫在 `sim/fx.ts` 的 `dropOrb` 那一段）。
     Boss 的 `hpMul` 是 1，所以牠的經驗也維持基礎值 —— 那是刻意的，
     Boss 的獎勵走金箱不走經驗球。 */
  e.xpVal = Math.max(1, Math.round(T.xp * hpMul));
  e.dmgMul = dmgMulAt(STATE.t);
  var sp = safeSpot(x, z, T.r + 0.4);
  e.pos.set(sp.x, 0, sp.z); e.vel.set(0, 0, 0);
  e.prevX = sp.x; e.prevZ = sp.z;   // 否則第一次內插會從上一隻的舊座標飛過來
  /* 貼地高度。**生成點就要問一次** —— 從 0 開始的話，生在高台上的敵人
     第一幀會出現在地板裡再彈上來（池化重用時上一隻的高度也會殘留）。 */
  e.y = groundYAt(sp.x, sp.z);
  e.root.position.set(sp.x, e.y, sp.z);
  e.flash = 0; e.st = 0; e.ph = 0; e.cd = rr(0.4, 1.4); e.cd2 = rr(2, 4);
  e.impact = 0; e.impactIdle = 0; e.staggerT = 0; e.staggerCd = 0;
  /* 池化重用的重置。精英是「升格」出來的（改的是 instance 欄位），
     死掉回池之後這幾個欄位會留著 —— 不清的話下一隻普通遊蕩者
     會頂著 14 倍血、1.7 倍體型與精英的發光跑出來。 */
  e.elite = false; e.eliteLead = false; e.impCap = undefined; e.eliteScale = 1; e.r = T.r;
  e.marchT = 0; e.marchX = 0; e.marchZ = 0;
  e.noSplit = false; e.detonated = false;
  /* 召喚認養（`sim/boss-ai.ts` 的護盾要數得出來哪幾隻是 Boss 召的）。
     池化物件不清的話，下一場的雜兵會被上一場的 MUTTER 認領。 */
  e.bornBy = null;
  /* TTK 埋點的起點（`sim/metrics.ts`）。池化物件不清的話下一隻會繼承
     上一隻的起點，量出來的 TTK 是一個沒有意義的大數字。 */
  e.metT0 = undefined; e.metHits = 0; e.metMax = 0;
  /* 小怪的變體（規格 §5.3）。**只改比例與行為數值**，不加 primitive、不改顏色。
     `eliteScale` 在這裡兼任「出場放大的目標尺度」—— 精英升格會再乘一次。
     碰撞半徑跟著體型走，否則「突刺型看起來比較大但打起來一樣」。 */
  e.tel = 0; e.atk = 0; e.mv = 0;
  e.buffT = 0; e.buffDmg = 1; e.buffSpd = 1; e.buffArm = 1;
  /* 載荷的暫存欄位（`sim/enemy-atk.ts`）。不清的話下一隻普通的 BAR 會頂著
     兩倍的接觸判定跑出來、或是一生出來就在拉玩家 —— 而且零錯誤。 */
  e.spinR = 0; e.pullT = 0; e.tgx = 0; e.tgz = 0;
  if (T.vars) {
    var V = rollVariant(T, variantWeights(STATE.t));
    e.vi = V.vi; e.q = V.q; e.vSpd = V.sp;
    /* `eliteScale` 是「出場放大要長到多大」。體積倍率乘在這裡而不是
       每個部件上 —— 它是一個全域的視覺決定，不該散進 175 個 primitive。
       碰撞半徑**不吃** `bodyK`（使用者 2026-08-03 的兩個決定各自獨立：
       體積放大是視覺、碰撞維持舊量級是手感）。 */
    e.eliteScale = V.sc * (T.bodyK || 1); e.r = T.r * V.sc;
  }
  /* Boss：階段、無敵、護盾、可破壞部位全部重置。**一場只出現一次，
     但池化物件是跨場重用的** —— 不清的話第二場的 WALZE 會頂著上一場
     打壞的腿、而且一出場就是二階段。 */
  if (T.boss) {
    e.stg = 0; e.invulnT = 0; e.shield = 0; e.shieldT = 0; e.adds = 0;
    e.airborne = T.fly ? 1 : 0; e.mv = 0;
    /* 體型是 `bossFieldScale()` 算的（`data/boss-stats.ts`）：小型 Boss 與小怪
       同樣 ×`BODY_K`，大型的會被壓縮 —— 48 單位高的 KAISER 框不進戰場鏡頭。
       **圖鑑讀同一個函式**，所以圖鑑裡看到的大小就是戰場上的大小，
       玩家在圖鑑建立的體型印象不會一進遊戲就作廢。
       `eliteScale` 在這裡兼任「出場放大的目標尺度」。 */
    e.eliteScale = T.bodyK || 1;
    resetBossParts(e);
  }
  if (e.mats && e.mats[1]) e.mats[1].emissiveIntensity = 1.6;
  if (e.eliteMark) e.eliteMark.g.visible = false;
  e.stT = 0; e.spiralA = 0; e.phase = rr(0, TAU);
  /* **每一隻的步態相位與動畫時鐘都要錯開**（原型 `newActor`：
     `ph = random()`、`t = random()*9`）。全部從 0 起跑的話，同一型的
     三百隻會完全同步 —— 腿一起擺、環一起轉、呼吸一起起伏，看起來像一片
     複製貼上的地毯而不是一群東西。這是同屏三百隻時最明顯的一條。

     偏移**從已經抽好的 `e.phase` 推導**，不另外抽 RNG：多抽一次會推移
     整條亂數序列，同一顆種子的一場就跟舊版對不起來（黃金雜湊會變），
     而這純粹是視覺的東西，不值得動到決定性基準。 */
  e.ph = e.phase / TAU;
  e.tOff = e.phase * 1.43;
  e.side = RNG.next() < 0.5 ? -1 : 1;
  e.face = Math.atan2(P.pos.x - sp.x, P.pos.z - sp.z);
  e.root.rotation.y = e.face; e.root.scale.setScalar(0.01);
  attachEnemy(e); enemies.push(e);
  /* 圖鑑：遇到一隻就記一次。**生成時記而不是進到視野時記** ——
     「進到視野」要每幀查距離，而且玩家背後生成的那一隻也算遇到過。 */
  logSeen(typeName);
  spawnRing(sp.x, sp.z, T.accent, 0.5, T.r * 3, 0.4);
  return e;
}
export function spawnAtRing(typeName, minD?, maxD?) {
  var a = rr(0, TAU), d = rr(minD || 32, maxD || 42);
  var x = P.pos.x + Math.cos(a) * d, z = P.pos.z + Math.sin(a) * d;
  var sc = clampArena(x, z, 6);
  x = sc.x; z = sc.z;
  return spawnEnemy(typeName, x, z);
}
/** 目標同屏數 —— **這條曲線就是難度曲線**。

    改成「填到目標」而不是「每隔多久放幾隻」，是因為壓力診斷
    （`pressure.test.ts`）量到舊寫法在玩家真的打架時場上只有 2–32 隻，
    而上限是 300：生成器每秒約 1.5 隻，玩家每秒殺 3–6 隻，**場永遠是空的**。
    「每隔 X 秒放 N 隻」的參數跟玩家的清場速度無關，所以永遠對不準；
    「補到 N 隻」則不論玩家多強都成立。

    對照 VS（`.claude/notes/vs-timeline.md`）：1:00 每波最低 30 隻、
    2:00 是 50 隻/0.5 秒、11:00 之後是 300 隻/0.1 秒 —— 它幾乎全程頂著軟上限。 */
export function targetField(t) {
  /* 二次式而不是線性。第一版用線性（14 + t×0.21），開場就 28 隻、
     3 分鐘 52 隻 —— 等於把整條曲線往前推了四分鐘，bot 3 分鐘就死。
     倖存者類的開場**必須空**：那幾分鐘是玩家認識武裝與撿等級的時間。

     **M12 把中後段整條抬起來**（roadmap 0.3 診斷 3：怪物密集度太低）。
     舊曲線第 10 分鐘只有 103 隻，而那時玩家的清場速度已經遠超過它 ——
     畫面上看起來就是「打完了，等下一批」。前 3 分鐘刻意維持原樣：
     密度是難度，但教學期的空曠是刻意的。

     二次項**從第 1.2 分鐘才開始算**。讓它從 0 起算的話 4–8 分鐘會整個被抬高，
     五顆種子的壓力診斷中位數會從 7.7 分掉到 5.9 分 —— 開場那幾分鐘
     是玩家認識武裝與撿等級的時間，不該一起變難。

     **2026-08-02 第二次上調**（使用者：「怪物密度也是偏低，參考 VS
     都是一大波的，能感受到震撼」）。對照 VS 的 Mad Forest：
     11:00 之後是「每 0.1 秒 300 隻」，它幾乎全程頂著軟上限 ——
     我們的第 10 分鐘只有 153 隻，那個數字撐不起「震撼」。

     **但第一版的上調把前四分鐘也一起抬了**：五顆種子的壓力診斷跑出
     1.5 / 2.8 / 3.9 分的早死（另外兩顆是 16 分、18 分）——
     那不是難度曲線，那是「開場就被淹掉」。二次項的起點退到 2.5 分、
     線性項降到 4，換來的是「前四分鐘接近改動前、第 8 分鐘之後爆炸」。

       0:00  18 隻      5:00   54 隻      12:00  300 隻（基礎上限）
       2:00  26 隻     10:00  204 隻      ＋編隊最多再疊 120 隻 */
  /* **2026-08-05（P5）：開場那五分鐘再抽薄一次。** 基礎項 18 → 10、
     線性項 4 → 4.5，兩者在第 10 分鐘之後幾乎重合（202 vs 204），
     動到的只有前五分鐘：0:00 從 18 隻降到 10 隻、2:00 從 26 降到 19。

     理由是 P5 把開場的雜兵血量拉成「要打三發才死」（`data/pacing.ts`）。
     01 §四的「每隻怪都是威脅」假設的是玩家一次面對少數幾隻 ——
     同屏 20 隻而每隻要三發，五顆種子的存活中位數從 7.4 分掉到 3.5 分。
     「開場必須空」本來就是這個檔上面那段註解寫的設計意圖，這次是把它做足。 */
  var m = t / 60, q = Math.max(0, m - 2.5);
  /* **上限要留給編隊 120 隻的餘裕。** 基礎生成頂到硬上限的話，
     第 11 分鐘之後每一波編隊都會被 `spawnEnemy` 靜默吃掉（回 null）——
     波次的節奏正好在最需要它的後半消失，而且沒有任何錯誤。
     實測：不留餘裕時第 11–20 分鐘的同屏峰值是一條 421 的直線。 */
  /* 密度倍率查表（`data/pacing.ts` 的 `DENSITY_MUL`）。**乘在這裡而不是
     `fieldTarget`**：那一支是「Boss 稀釋 × 怪潮」兩個臨時倍率的乘積，
     而這一條是基礎曲線的一部分 —— 混在一起的話怪潮的 ×2.5 會疊在
     補償倍率上面，那不是同一件事。 */
  return Math.min(ENEMY_CAP - 120,
    Math.round((10 + m * 4.5 + q * q * 2.6) * densityMulAt(t)));
}

/* ---------- Boss 戰的場面稀釋 ----------

   `.claude/notes/boss-fight-design.md`（使用者 2026-08-03 定案）：
   **稀釋而不是清空**。五場 Boss 戰全部超過 30 秒，所以一場都不清場 ——
   壓生成率但保留 on-kill、經驗迴圈與四色壓力，核心迴圈不能斷。

   係數是每一隻自己的 `dilute`（0.28–0.38，寫在 `data/boss-stats.ts`）。 */
export var FIELD = { mul: 1 };

/** 目標同屏數（吃 Boss 稀釋與怪潮）。生成器一律用這個，不要直接用 `targetField`。

    **兩個倍率各自獨立、相乘**：Boss 的稀釋（`FIELD.mul`）與怪潮
    （`HORDE.mul`，`sim/horde.ts`）會同時成立，共用一個欄位的話
    後設的那一個會把前一個蓋掉 —— 而 Boss 戰結束時的還原會把怪潮一起關掉。 */
export function fieldTarget(t) { return Math.round(targetField(t) * FIELD.mul * HORDE.mul); }

/** Boss 降臨／死亡時切換稀釋。降臨時**順手把場上多的清掉** ——
    只停生成的話，場上那 300 隻要等玩家慢慢殺，「Boss 來了場面變乾淨」
    這件事在畫面上要一分鐘才發生，等於沒發生。

    清掉的是**離玩家 34 單位以外**的：在畫面裡憑空消失的話，
    玩家會覺得是 bug 而不是演出。 */
export function setFieldMul(k) {
  FIELD.mul = k;
  if (k >= 1) return;
  var want = fieldTarget(STATE.t);
  for (var i = enemies.length - 1; i >= 0 && enemies.length > want; i--) {
    var e = enemies[i];
    if (e.T.boss || e.elite) continue;                    // 精英與 Boss 是設計排進去的
    if (Math.hypot(e.pos.x - P.pos.x, e.pos.z - P.pos.z) < 34) continue;
    removeEnemy(e);
  }
}

export function updateSpawner(dt) {
  STATE.spawnT -= dt;
  if (STATE.spawnT > 0) return;
  STATE.spawnT = 0.35;
  var gap = fieldTarget(STATE.t) - enemies.length;
  if (gap <= 0) return;
  /* 一次補的上限：缺口再大也不會瞬間變出一堵牆。
     場上被清空（大招）之後要看得到「敵人重新湧上來」的過程，
     而不是下一幀就滿了。

     **但補得太慢等於曲線是假的。** 舊值是 `3 + diff×2.5`（第 10 分鐘約
     26 隻/秒），而同期玩家的清場速度已經到 40 隻/秒 —— 目標訂 159 隻，
     場上實際只會停在四、五十隻。這個係數要**大於玩家的清場速度**，
     目標值才是真的上限；不然調的是一個永遠達不到的數字。

     所以補位速度改成**跟著目標值走**（每次補上目標的 10%，約 3.5 秒回滿），
     而不是跟著時間走。寫死一個絕對值會兩頭不討好：
     早期 14 隻的場用「每秒 11 隻」補等於清了也跟沒清一樣
     （實測 bot 從 7.7 分中位數掉到 2.9 分，死因是清場後場立刻又滿），
     晚期 280 隻的場用同一個值又補不動。 */
  var burst = Math.min(gap, 1 + Math.ceil(fieldTarget(STATE.t) * 0.1));
  for (var i = 0; i < burst; i++) spawnAtRing(rollType(STATE.t));
}
